import { useCallback, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import DataContext from "../../../../../context/DataContext";
import useService from "../../../../../hooks/useService";

export default function useViewNavigationService() {
  const navigate = useNavigate();
  const viewAccessService = useService("view-access-service");
  const { userRoles } = useContext(DataContext);
  const permissions = useMemo(() => userRoles?.permissions ?? [], [userRoles]);

  const hasPermissionForView = useCallback(
    (viewName) => viewAccessService.hasPermissionForView(viewName, permissions),
    [permissions, viewAccessService]
  );

  const canAccessView = useCallback(
    (viewName) => viewAccessService.canAccessView(viewName, permissions),
    [permissions, viewAccessService]
  );

  const navigateToNotFound = useCallback(
    (replace = true) => {
      navigate(viewAccessService.getNotFoundPath(), { replace });
    },
    [navigate, viewAccessService]
  );

  const ensureCanAccessView = useCallback(
    (viewName, replace = true) => {
      if (!canAccessView(viewName)) {
        navigateToNotFound(replace);
        return false;
      }

      return true;
    },
    [canAccessView, navigateToNotFound]
  );

  const navigateToView = useCallback(
    (viewName, id, options = {}) => {
      if (!ensureCanAccessView(viewName, true)) {
        return false;
      }

      const path = viewAccessService.buildViewPath(viewName, id);

      if (options.newTab) {
        window.open(path, "_blank", "noopener,noreferrer");
        return true;
      }

      navigate(path, options);
      return true;
    },
    [ensureCanAccessView, navigate, viewAccessService]
  );

  return {
    isValidViewName: viewAccessService.isValidViewName.bind(viewAccessService),
    hasPermissionForView,
    canAccessView,
    ensureCanAccessView,
    navigateToView,
    navigateToNotFound,
  };
}
